import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import http from "../api/apiClient";
import { GiteaIssueOwnerSelect, IssueOwner } from "./GiteaIssueOwnerSelect";

export function GiteaCreateIssue({ onCreated, sourceNumber }: { onCreated: () => void; sourceNumber?: string | number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [owner, setOwner] = useState<IssueOwner | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const label = sourceNumber != null ? t("gitea.split.action", { defaultValue: "Split issue" }) : t("gitea.create.action", { defaultValue: "Create issue" });
  const inputClass = "mt-1 w-full rounded-lg border border-gray-300 bg-white p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100";
  const start = async () => {
    setMessage(""); setError(""); setOpen(true);
    if (sourceNumber == null) return;
    setLoading(true);
    setTitle(""); setDescription(""); setOwner(null);
    try {
      const response = await http.get(`/admin/gitea/issues/${encodeURIComponent(String(sourceNumber))}/split`);
      setTitle(response.data.title || "");
      setDescription(response.data.description || "");
      setOwner(response.data.owner || null);
    } catch (e: any) {
      setError(e?.response?.data?.message || t("gitea.split.load_failed", { defaultValue: "The original issue could not be loaded. Close this form and try again." }));
    } finally { setLoading(false); }
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (loading || busy.current || (sourceNumber == null && !owner) || !title.trim() || !description.trim()) return;
    busy.current = true;
    setSaving(true);
    setError("");
    try {
      const endpoint = sourceNumber == null ? "/admin/gitea/issues" : `/admin/gitea/issues/${encodeURIComponent(String(sourceNumber))}/split`;
      const response = await http.post(endpoint, { title: title.trim(), description: description.trim(), ...(sourceNumber == null ? { ownerSub: owner!.sub } : {}) });
      const created = sourceNumber == null ? response.data : response.data.issue;
      setMessage(sourceNumber != null && !response.data.sourceCommentAdded
        ? t("gitea.split.partial", { defaultValue: "Issue #{{number}} created. The reference could not be added to the original issue.", number: created.number })
        : t("gitea.create.success", { defaultValue: "Issue #{{number}} created.", number: created.number }));
      setOpen(false);
      setTitle(""); setDescription(""); setOwner(null);
      onCreated();
    } catch (e: any) {
      setError(e?.response?.data?.message || t("gitea.create.failed", { defaultValue: "The issue could not be created." }));
    } finally { busy.current = false; setSaving(false); }
  };
  return <>
    <button type="button" className="rounded-xl bg-indigo-600 px-4 py-2 font-semibold text-white" disabled={loading} onClick={() => void start()}>{label}</button>
    {message && <span role="status">{message}</span>}
    {open && <Dialog open={open} onClose={() => { if (!busy.current && !loading) setOpen(false); }} className="fixed inset-0 z-[10000] flex items-center justify-center overflow-y-auto bg-black/40 p-4">
      <DialogPanel className="max-h-[90dvh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 text-gray-900 shadow-xl dark:bg-gray-950 dark:text-gray-100" onKeyDown={(event) => { if (event.key === "Escape" && !saving && !loading) setOpen(false); }}>
        <DialogTitle className="text-xl font-semibold">{label}</DialogTitle>
        {sourceNumber != null && <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{t("gitea.split.help", { defaultValue: "Keep only the part for the new issue below. The original issue, its comments and attachments will be preserved." })}</p>}
        {loading && <p role="status">{t("gitea.split.loading", { defaultValue: "Loading original issue…" })}</p>}
        <form onSubmit={submit} className="mt-4 space-y-4">
          <fieldset disabled={saving || loading} className="space-y-4">
            <div><p className="mb-1">{t("gitea.create.reporter", { defaultValue: "Reporter" })}</p>
              {sourceNumber != null
                ? <p>{owner?.name}{owner?.email ? ` (${owner.email})` : ""}</p>
                : <GiteaIssueOwnerSelect ownerName={owner?.name || t("gitea.create.choose_reporter", { defaultValue: "Choose reporter" })} ownerEmail={owner?.email} onChanged={setOwner} />}

            </div>
            <label className="block">{t("gitea.create.title", { defaultValue: "Title" })}<input autoFocus required value={title} onChange={(event) => setTitle(event.target.value)} className={inputClass} /></label>
            <label className="block">{t("gitea.create.description", { defaultValue: "Description" })}<textarea required rows={6} value={description} onChange={(event) => setDescription(event.target.value)} className={inputClass} /></label>
          </fieldset>
          {error && <p role="alert" className="text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" disabled={saving || loading} onClick={() => setOpen(false)}>{t("gitea.create.cancel", { defaultValue: "Cancel" })}</button>
            <button type="submit" disabled={saving || loading || (sourceNumber == null && !owner) || !title.trim() || !description.trim()} className="rounded-lg bg-indigo-600 px-4 py-2 text-white disabled:opacity-50">{saving ? t("gitea.create.saving", { defaultValue: "Creating…" }) : label}</button>
          </div>
        </form>
      </DialogPanel>
    </Dialog>}
  </>;
}
